/**
 * Invoice Template Settings
 * Extracted from RevOpsPage.tsx SettingsTab for shared use.
 * Manages template grid with CRUD, duplicate, set default, and delete.
 */

import React, { useState } from 'react';
import {
  Plus,
  Edit,
  Copy,
  CheckCircle,
  Trash2,
  Calculator,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  useRevOps,
  InvoiceTemplate,
} from '@/contexts/RevOpsContext';
import { TemplateEditorModal } from './TemplateEditorModal';

export function InvoiceTemplateSettings() {
  const {
    invoiceTemplates,
    deleteTemplate,
    duplicateTemplate,
    setDefaultTemplate,
  } = useRevOps();

  const [editTemplateOpen, setEditTemplateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplate | null>(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<InvoiceTemplate | null>(null);

  const handleEditTemplate = (template: InvoiceTemplate) => {
    setSelectedTemplate(template);
    setIsNewTemplate(false);
    setEditTemplateOpen(true);
  };

  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setIsNewTemplate(true);
    setEditTemplateOpen(true);
  };

  const handleDuplicate = (template: InvoiceTemplate) => {
    const newTemplate = duplicateTemplate(template.id);
    if (newTemplate) {
      setSelectedTemplate(newTemplate);
      setIsNewTemplate(false);
      setEditTemplateOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (templateToDelete) {
      deleteTemplate(templateToDelete.id);
      setTemplateToDelete(null);
      setDeleteConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Invoice Templates Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Invoice Templates</h3>
            <p className="text-sm text-gray-500 mt-1">
              Create and manage invoice templates with custom branding and payment details
            </p>
          </div>
          <button
            onClick={handleNewTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            New Template
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {invoiceTemplates.map((template) => (
            <div
              key={template.id}
              className={`border rounded-lg p-4 ${
                template.isDefault ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: template.primaryColor }}
                  >
                    {template.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{template.name}</h4>
                    <p className="text-xs text-gray-500">{template.companyName}</p>
                  </div>
                </div>
                {template.isDefault && (
                  <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                    Default
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: template.primaryColor }} />
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: template.secondaryColor }} />
                </div>
                <span>|</span>
                <span>{template.invoicePrefix || 'INV'}-XXX</span>
                <span>|</span>
                <span>Due: {template.defaultDueDays || 30} days</span>
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                <button
                  onClick={() => handleEditTemplate(template)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded"
                >
                  <Edit className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => handleDuplicate(template)}
                  className="flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                {!template.isDefault && (
                  <>
                    <button
                      onClick={() => setDefaultTemplate(template.id)}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      title="Set as default"
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        setTemplateToDelete(template);
                        setDeleteConfirmOpen(true);
                      }}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tax Configuration Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Tax Configuration</h3>
            <p className="text-sm text-gray-500 mt-1">
              Automatic tax calculation based on jurisdiction
            </p>
          </div>
        </div>

        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Calculator className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800">Automatic Tax Calculation</h4>
              <p className="text-sm text-blue-600 mt-1">
                Taxes are automatically calculated based on the customer's billing address and service location.
                US state sales tax rates are applied according to the destination state.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {['CA: 7.25%', 'NY: 8.0%', 'TX: 6.25%', 'FL: 6.0%', 'WA: 6.5%'].map((rate) => (
                  <span key={rate} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    {rate}
                  </span>
                ))}
                <span className="text-xs text-blue-600">+ more states...</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Tax-Exempt Services</h4>
          <p className="text-sm text-gray-500">
            Healthcare, education, and nonprofit services are automatically marked as tax-exempt.
          </p>
        </div>
      </div>

      {/* Template Editor Modal */}
      <TemplateEditorModal
        isOpen={editTemplateOpen}
        onClose={() => {
          setEditTemplateOpen(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        isNew={isNewTemplate}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={() => setDeleteConfirmOpen(false)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              onClick={() => setDeleteConfirmOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
